import { useEffect, useMemo, useRef, useState } from 'react';

function AppSelect({
  options = [],
  value,
  onChange,
  placeholder = 'Selecione...',
  className = '',
  disabled = false,
  ariaLabel = 'Selecionar opção',
}) {
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const normalizedOptions = useMemo(
    () => options.map((option) => ({ ...option, stringValue: String(option.value) })),
    [options]
  );

  const selectedIndex = normalizedOptions.findIndex(
    (option) => option.stringValue === String(value)
  );
  const selectedOption = selectedIndex >= 0 ? normalizedOptions[selectedIndex] : null;

  useEffect(() => {
    if (!open) return;
    setActiveIndex(-1);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open]);

  const selectByIndex = (index) => {
    const next = normalizedOptions[index];
    if (!next) return;
    onChange(next.value);
    setOpen(false);
  };

  const handleTriggerKeyDown = (event) => {
    if (disabled) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      setActiveIndex((prev) => Math.min(prev + 1, normalizedOptions.length - 1));
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      setActiveIndex((prev) => Math.max(prev - 1, 0));
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      if (activeIndex >= 0) {
        selectByIndex(activeIndex);
      }
      return;
    }

    if (event.key === 'Escape') {
      setOpen(false);
    }
  };

  const rootClassName = [
    'app-select',
    open ? 'is-open' : '',
    disabled ? 'is-disabled' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div ref={rootRef} className={rootClassName}>
      <button
        type="button"
        className="app-select-trigger"
        onClick={() => !disabled && setOpen((prev) => !prev)}
        onKeyDown={handleTriggerKeyDown}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="app-select-value">{selectedOption?.label || placeholder}</span>
        <i className={`bi ${open ? 'bi-chevron-up' : 'bi-chevron-down'} app-select-caret`}></i>
      </button>

      {open && (
        <ul className="app-select-menu" role="listbox" aria-label={ariaLabel}>
          {normalizedOptions.map((option, index) => {
            const isSelected = index === selectedIndex;
            const isActive = index === activeIndex;

            return (
              <li key={`${option.stringValue}-${index}`} role="option" aria-selected={isSelected}>
                <button
                  type="button"
                  className={`app-select-option ${isSelected ? 'is-selected' : ''} ${isActive ? 'is-active' : ''}`}
                  onClick={() => selectByIndex(index)}
                  onMouseEnter={() => setActiveIndex(index)}
                >
                  {option.label}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default AppSelect;
